import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ChartBarIcon,
    ChatAlt2Icon,
    MailIcon,
    CollectionIcon,
    SwitchHorizontalIcon,
    GlobeAltIcon,
    MapIcon,
    TrendingUpIcon,
    CheckIcon,
    ClipboardCheckIcon,
} from '@heroicons/react/outline';
import ModalWrapper from 'components/ModalWrapper';
import PropTypes from 'prop-types';

export const ALL_SECTIONS = [
    'overview',
    'conversationQuality',
    'conversationsPerDay',
    'messagesPerDay',
    'byService',
    'byType',
    'byLanguage',
    'od',
    'kpiSection',
];

const SECTION_ICONS = {
    overview:           ChartBarIcon,
    conversationQuality: ClipboardCheckIcon,
    conversationsPerDay: ChatAlt2Icon,
    messagesPerDay:     MailIcon,
    byService:          CollectionIcon,
    byType:             SwitchHorizontalIcon,
    byLanguage:         GlobeAltIcon,
    od:                 MapIcon,
    kpiSection:          TrendingUpIcon,
};

function ExportSectionPickerModal({ open, onClose, onExport, isExporting }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(new Set(ALL_SECTIONS));

    useEffect(() => {
        if (open) setSelected(new Set(ALL_SECTIONS));
    }, [open]);

    const toggle = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const allChecked  = selected.size === ALL_SECTIONS.length;
    const noneChecked = selected.size === 0;

    const toggleAll = () => {
        setSelected(allChecked ? new Set() : new Set(ALL_SECTIONS));
    };

    return (
        <ModalWrapper open={open} setOpen={onClose} title={t('analytics.export.picker.title')}>
            <div className="w-full sm:w-[540px]">

                {/* Header row — select-all + counter */}
                <div className="flex items-center justify-between mb-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="w-4 h-4 accent-green-600"
                            checked={allChecked}
                            ref={(el) => {
                                if (el) el.indeterminate = !allChecked && !noneChecked;
                            }}
                            onChange={toggleAll}
                        />
                        <span className="text-sm font-semibold text-gray-700">
                            {t('analytics.export.picker.select_all')}
                        </span>
                    </label>
                    <span className="text-xs text-gray-400 font-medium tabular-nums">
                        {selected.size} / {ALL_SECTIONS.length} {t('analytics.export.picker.selected')}
                    </span>
                </div>

                {/* Card grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                    {ALL_SECTIONS.map((key) => {
                        const Icon = SECTION_ICONS[key];
                        const isSelected = selected.has(key);
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => toggle(key)}
                                className={`relative flex items-center gap-3 p-1 rounded-xl border-2 text-left transition-all duration-150
                                    ${isSelected
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <div className={`flex-shrink-0 p-2 m-1 rounded-lg
                                    ${isSelected ? 'bg-green-100' : 'bg-gray-100'}`}
                                >
                                    <Icon className={`w-5 h-5 ${isSelected ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <span className={`text-sm font-medium leading-tight
                                    ${isSelected ? 'text-green-800' : 'text-gray-600'}`}
                                >
                                    {t(`analytics.export.picker.sections.${key}`)}
                                </span>
                                {isSelected && (
                                    <span className="absolute top-2.5 right-2.5">
                                        <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                    >
                        {t('analytics.export.picker.cancel')}
                    </button>
                    <button
                        onClick={() => onExport([...selected])}
                        disabled={noneChecked || isExporting}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium transition
                            ${noneChecked || isExporting
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700'}`}
                    >
                        {isExporting ? (
                            <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                                {t('analytics.export.picker.exporting')}
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                {t('analytics.export.picker.export')}
                            </>
                        )}
                    </button>
                </div>

            </div>
        </ModalWrapper>
    );
}

ExportSectionPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  isExporting: PropTypes.bool,
};

export default ExportSectionPickerModal;
